class LoadMore {
  constructor(options = {}) {
    this.options = {
      containerSelector: '.products-grid',
      buttonSelector: '.load-more-btn', 
      loadingSelector: '.load-more-loading',
      endpoint: '/api/products/load-more',
      limit: 8,
      ...options
    };
    
    this.container = null;
    this.button = null;
    this.loadingIndicator = null;
    this.isLoading = false;
    this.cursor = null;
    this.hasNextPage = true;
    this.lazyLoadInstance = null;
    
    this.init();
  }

  init() {
    this.container = document.querySelector(this.options.containerSelector);
    this.button = document.querySelector(this.options.buttonSelector);
    this.loadingIndicator = document.querySelector(this.options.loadingSelector);
    
    if (!this.container || !this.button) {
      return;
    }

    // Set initial cursor from data attribute if available
    this.cursor = this.button.dataset.cursor || null;
    this.hasNextPage = this.button.dataset.hasNextPage === 'true';
    
    this.bindEvents();
    this.updateButtonState();
  }

  bindEvents() {
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.loadMore();
    });
  }

  async loadMore() {
    if (this.isLoading || !this.hasNextPage) {
      return;
    }

    this.setLoading(true);

    try {
      const response = await this.fetchMoreProducts();
      
      if (response.success) {
        this.appendProducts(response.products);
        this.cursor = response.pageInfo.endCursor;
        this.hasNextPage = response.pageInfo.hasNextPage;
        this.updateButtonState();
        
        // Trigger lazy loading for new products
        this.initializeLazyLoadForNewProducts();
      } else {
        console.error('Error loading more products:', response.error);
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async fetchMoreProducts() {
    const params = new URLSearchParams({
      cursor: this.cursor || '',
      limit: this.options.limit
    });

    const response = await fetch(`${this.options.endpoint}?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  appendProducts(products) {
    products.forEach((product, index) => {
      const productElement = this.createProductElement(product);
      this.container.appendChild(productElement);
    });
  }

  createProductElement(product) {
    // Create the wrapper div
    const wrapper = document.createElement('div');
    wrapper.className = 'lazy-load-product lazy-load opacity-0 translate-y-4';
    
    // Create the product card HTML
    wrapper.innerHTML = this.getProductCardHTML(product);
    
    return wrapper;
  }

  getProductCardHTML(product) {
    const defaultVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    const imageUrl = product.images && product.images.length > 0 ? product.images[0].url : 
                    (defaultVariant && defaultVariant.image ? defaultVariant.image.url : '');
    const imageAlt = product.images && product.images.length > 0 ? 
                    (product.images[0].altText || product.title) : 
                    (defaultVariant && defaultVariant.image ? (defaultVariant.image.altText || product.title) : product.title);
    
    const priceHTML = defaultVariant && defaultVariant.price ? 
      `${Math.round(defaultVariant.price * 100)} NOK${defaultVariant.compareAtPrice && defaultVariant.compareAtPrice > defaultVariant.price ? 
        `<span class="line-through text-gray-400 ml-2">${Math.round(defaultVariant.compareAtPrice * 100)} NOK</span>` : ''}` :
      'Pris på forespørsel';

    return `
      <div class="products-item col-span-6 md:col-span-4 lg:col-span-3">
        <a href="${product.url}" class="space-y-12 md:space-y-20 group">
          ${imageUrl ? `
            <figure class="rounded-lg overflow-hidden w-full h-fit">
              <img src="${imageUrl}" alt="${imageAlt}" class="w-full h-auto object-cover object-top select-none aspect-[5/7] scale-[1.025] group-hover:scale-[1.075] transition-all duration-500 ease-bounce">
            </figure>
          ` : `
            <div class="rounded-lg overflow-hidden w-full h-fit bg-gray-200 aspect-[5/7] flex items-center justify-center">No Image</div>
          `}
          
          <div class="w-full flex justify-between items-center">
            <div class="space-y-12">
              <h2 class="font-medium text-base md:text-md">${product.title}</h2>
              <div class="text-sm opacity-60">
                ${priceHTML}
              </div>
            </div>
          </div>
          <div class="font-medium text-sm border-b border-b-black/15 w-fit group-hover:text-blue group-hover:border-b-blue/15 transition-all duration-500 ease-bounce">Kjøp produkt</div>
        </a>
      </div>
    `;
  }

  initializeLazyLoadForNewProducts() {
    // Get all new products that haven't been animated yet
    const newProducts = this.container.querySelectorAll('.lazy-load-product.opacity-0');
    
    if (this.lazyLoadInstance && this.lazyLoadInstance.refresh) {
      // Use existing lazy load instance if available
      this.lazyLoadInstance.refresh();
    } else {
      // Fallback - trigger animations directly with stagger effect
      newProducts.forEach((element, index) => {
        const delay = index * 100;
        setTimeout(() => {
          element.classList.remove('opacity-0', 'translate-y-4');
          element.classList.add('opacity-100', 'translate-y-0');
        }, delay);
      });
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
    
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = loading ? 'block' : 'none';
    }
    
    this.button.disabled = loading;
    this.button.textContent = loading ? 'Laster...' : 'Last flere produkter';
  }

  updateButtonState() {
    if (!this.hasNextPage) {
      this.button.style.display = 'none';
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
    }
  }

  // Method to set lazy load instance reference
  setLazyLoadInstance(instance) {
    this.lazyLoadInstance = instance;
  }

  destroy() {
    if (this.button) {
      this.button.removeEventListener('click', this.loadMore);
    }
  }
}

export default LoadMore;
